import React, { useState } from "react";
import { toast } from "react-toastify";

import { DataProvider } from "../../../../features/auth/store/types/data-types";
import { MdHelpOutline, MdOutlineDelete } from "react-icons/md";
import { FiEdit3 } from "react-icons/fi";
import {
  fetchDataProviders,
  updateDataProvider,
} from "../../../../features/auth/store/dataProviderSlice";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../../redux/store";
import { StatusSwitch } from "../../../../components/Reusable/ToggleSwitch";
import { buildProviderStatusFormData } from "../../statusToggleUtils";

interface Props {
  providers: DataProvider[];
  loading: boolean;
  searchTerm: string;
  statusFilter: string;
  onDelete: (id: number) => void;
  onEdit: (id: number) => void;
  onManageFaq: (provider: { id: number; name: string }) => void;
}

const DataProvidersTable: React.FC<Props> = ({
  providers,
  loading,
  searchTerm,
  statusFilter,
  onDelete,
  onEdit,
  onManageFaq,
}) => {
  const {
    pagination,
    lastPage: totalPages,
    currentPage,
  } = useSelector((state: RootState) => state.dataProviders);
  const dispatch = useDispatch<AppDispatch>();
  const [updatingProviderIds, setUpdatingProviderIds] = useState<number[]>([]);
  const [updatingStockIds, setUpdatingStockIds] = useState<number[]>([]);
  // Calculate pagination values
  const { nextPageUrl, prevPageUrl } = pagination;

  // Handle page navigation
  const goToNextPage = () => {
    if (currentPage < totalPages && nextPageUrl !== null) {
      dispatch(
        fetchDataProviders({
          url: nextPageUrl,
          search: searchTerm.trim() || undefined,
          status: statusFilter || undefined,
        }),
      );
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1 && prevPageUrl !== null) {
      dispatch(
        fetchDataProviders({
          url: prevPageUrl,
          search: searchTerm.trim() || undefined,
          status: statusFilter || undefined,
        }),
      );
    }
  };

  const handleStatusToggle = async (provider: DataProvider) => {
    const nextStatus = provider.status === "on" ? "off" : "on";

    setUpdatingProviderIds((prev) => [...prev, provider.id]);

    try {
      const formData = new FormData();
      formData.append("name", provider.name);
      formData.append("slug", provider.slug || "");
      formData.append("description", provider.description || "");
      formData.append("status", nextStatus);
      formData.append("recipient_label", provider.recipient_label || "");
      formData.append("out_of_stock", provider.out_of_stock ? "1" : "0");
      formData.append(
        "verify_recipient",
        provider.verify_recipient ? "1" : "0",
      );
      formData.append(
        "additional_input",
        provider.additional_input ? "1" : "0",
      );
      formData.append(
        "additional_input_placeholder",
        provider.additional_input_placeholder || "",
      );
      formData.append(
        "additional_input_type",
        provider.additional_input_type || "",
      );
      formData.append(
        "additional_input_label",
        provider.additional_input_label || "",
      );
      formData.append(
        "additional_input_form_name",
        provider.additional_input_form_name || "",
      );

      await dispatch(
        updateDataProvider({
          id: provider.id,
          data: formData,
        }),
      ).unwrap();

      toast.success(
        `Provider ${nextStatus === "on" ? "activated" : "deactivated"} successfully`,
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to update provider status");
    } finally {
      setUpdatingProviderIds((prev) => prev.filter((id) => id !== provider.id));
    }
  };

  const handleOutOfStockToggle = async (dataProvider: DataProvider) => {
    const nextStockStatus = !dataProvider.out_of_stock;

    setUpdatingStockIds((prev) => [...prev, dataProvider.id]);
    const plan = { ...dataProvider, out_of_stock: nextStockStatus };

    try {
      await dispatch(
        updateDataProvider({
          id: dataProvider.id,
          data: buildProviderStatusFormData(plan, dataProvider.status),
        }),
      ).unwrap();
      toast.success(
        `Provider plans marked as ${nextStockStatus ? "out of stock" : "in stock"}`,
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to update stock status");
    } finally {
      setUpdatingStockIds((prev) =>
        prev.filter((id) => id !== dataProvider.id),
      );
    }
  };

  const hasProviders = providers.length > 0;

  return (
    <div className="flex flex-col w-full">
      <div className="overflow-x-auto pb-4">
        <table className="w-full min-w-[800px] border border-[#FFFFFF1A] rounded-2xl overflow-hidden font-Inter">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="px-3 md:px-6 text-left">Status</th>
              <th className="px-3 md:px-6 text-left">In Stock</th>
              <th className="px-3 md:px-6 text-left">Name</th>
              <th className="px-3 md:px-6 text-left hidden md:table-cell">
                Slug
              </th>
              <th className="px-3 md:px-6 text-left hidden md:table-cell">
                Description
              </th>
              <th className="px-3 md:px-6 text-left hidden md:table-cell">
                Verify Recipient
              </th>
              <th className="px-3 md:px-6 text-left">Image</th>
              <th className="px-3 md:px-6 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
            {loading ? (
              <tr className="border-t h-[80px]">
                <td colSpan={7} className="p-4 text-center">
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                </td>
              </tr>
            ) : hasProviders ? (
              providers.map((provider) => (
                <tr
                  key={provider.id}
                  className="border-b border-gray-200 h-[50px]"
                >
                  <td className="px-3 md:px-6 py-2 align-middle">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={provider.status === "on"}
                        disabled={updatingProviderIds.includes(provider.id)}
                        onChange={() => handleStatusToggle(provider)}
                        aria-label={`Toggle ${provider.name} status`}
                      />
                      <div className="relative h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-green-500 peer-disabled:opacity-50 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-transform after:content-[''] peer-checked:after:translate-x-full" />
                      <span className="text-xs font-medium normal-case">
                        {updatingProviderIds.includes(provider.id)
                          ? "Updating..."
                          : provider.status === "on"
                            ? "On"
                            : "Off"}
                      </span>
                    </label>
                  </td>
                  <td className="px-6 py-4 align-middle">
                    <StatusSwitch
                      checked={!provider.out_of_stock}
                      disabled={updatingStockIds.includes(provider.id)}
                      onChange={() => handleOutOfStockToggle(provider)}
                      label={`Toggle ${provider.name || "plans"} stock status`}
                      activeText="Available"
                      inactiveText="Unavailable"
                    />
                  </td>
                  <td className="px-3 md:px-6 py-2 align-middle truncate max-w-[100px]">
                    {provider.name}
                  </td>
                  <td className="px-6 py-4 align-middle hidden md:table-cell">
                    {provider.slug || "-"}
                  </td>
                  <td className="px-3 md:px-6 py-2 align-middle hidden md:table-cell max-w-[200px] truncate">
                    {provider.description ? (
                      <div
                        className="[&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:no-underline"
                        dangerouslySetInnerHTML={{
                          __html: provider.description,
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </td>

                  <td className="px-3 md:px-6 py-2 align-middle hidden md:table-cell">
                    {provider.verify_recipient ? "Yes" : "No"}
                  </td>
                  <td className="px-3 md:px-6 py-2 align-middle">
                    {provider.image_url ? (
                      <img
                        src={provider.image_url}
                        alt={provider.name}
                        className="w-8 h-8 object-cover rounded"
                      />
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-3 md:px-6 py-2 align-middle">
                    <div className="flex items-center gap-2 h-full">
                      <button
                        className="hover:text-gray-600 transition-colors"
                        onClick={() =>
                          onManageFaq({ id: provider.id, name: provider.name })
                        }
                        title="Manage FAQ"
                      >
                        <MdHelpOutline className="text-xl" />
                      </button>
                      <button
                        className="text-blue-600"
                        onClick={() => onEdit(provider.id)}
                      >
                        <FiEdit3 className="text-xl" />
                      </button>
                      <button
                        className="text-red-600"
                        onClick={() => onDelete(provider.id)}
                      >
                        <MdOutlineDelete className="text-xl" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="border-gray-200 h-[50px]">
                <td colSpan={7} className="p-4 text-center">
                  No providers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center border-t border-[#FFFFFF1A]">
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={goToPreviousPage}
          disabled={loading || currentPage === 1 || !prevPageUrl}
        >
          Previous
        </button>
        <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={goToNextPage}
          disabled={
            loading ||
            currentPage === totalPages ||
            totalPages === 0 ||
            !nextPageUrl
          }
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default DataProvidersTable;
